import { useState, useRef, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Slider,
    IconButton,
    Paper,
    CircularProgress,
    Tooltip,
    createTheme,
    ThemeProvider
} from '@mui/material';
import {
    PhotoCamera as CaptureIcon
} from '@mui/icons-material';

// Create LTR theme for slider only
const ltrTheme = createTheme({
    direction: 'ltr',
});

const VideoThumbnailExtractor = ({
    videoFile,
    videoUrl, // New prop for existing video URLs (S3, etc.)
    onThumbnailChange,
    thumbnailPreview: externalThumbnailPreview,
    disabled = false,
    initialTime = 0,
    showManualUpload = true,
    aspectRatio = 'landscape' // 'landscape' (16:9) or 'square' (1:1)
}) => {
    // Local state
    const [videoSource, setVideoSource] = useState(null); // URL to play (either from file or URL prop)
    const [videoMetadata, setVideoMetadata] = useState(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [, setThumbnailPreview] = useState(externalThumbnailPreview || null);
    const [isExtracting, setIsExtracting] = useState(false);
    const [, setIsManualUpload] = useState(false);
    const [error, setError] = useState(null);
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);

    // Refs
    const videoRef = useRef(null);
    const debounceRef = useRef(null);
    const objectUrlRef = useRef(null);

    // Cleanup object URL on unmount
    useEffect(() => {
        return () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
            }
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    // Sync external thumbnail preview with local state
    useEffect(() => {
        if (externalThumbnailPreview !== undefined) {
            setThumbnailPreview(externalThumbnailPreview);
        }
    }, [externalThumbnailPreview]);

    // Determine video source (file takes priority over URL)
    useEffect(() => {
        // Cleanup previous object URL if we created one
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }

        setIsVideoLoaded(false);
        setVideoMetadata(null);

        if (videoFile) {
            const objectUrl = URL.createObjectURL(videoFile);
            objectUrlRef.current = objectUrl;
            setVideoSource(objectUrl);
        } else if (videoUrl) {
            setVideoSource(videoUrl);
        } else {
            setVideoSource(null);
            setThumbnailPreview(externalThumbnailPreview || null);
        }
    }, [videoFile, videoUrl]);

    // Handle video metadata loaded
    const handleVideoLoaded = useCallback(() => {
        if (videoRef.current) {
            const metadata = {
                duration: videoRef.current.duration,
                width: videoRef.current.videoWidth,
                height: videoRef.current.videoHeight
            };
            setVideoMetadata(metadata);
            setIsVideoLoaded(true);

            // Set initial time and seek
            const seekTime = Math.min(initialTime, metadata.duration);
            setCurrentTime(seekTime);
            videoRef.current.currentTime = seekTime;

            // Auto-extract thumbnail for new video files (only if no existing thumbnail)
            if (videoFile && !externalThumbnailPreview) {
                setTimeout(() => extractThumbnailFromVideo(seekTime), 100);
            }
        }
    }, [videoFile, initialTime, externalThumbnailPreview]);

    // Extract thumbnail from video element at specific time
    const extractThumbnailFromVideo = useCallback(async (time) => {
        if (!videoRef.current || !isVideoLoaded) return;

        setIsExtracting(true);
        setError(null);

        try {
            const video = videoRef.current;

            // Seek to the specified time
            video.currentTime = time;

            // Wait for seek to complete
            await new Promise((resolve) => {
                const onSeeked = () => {
                    video.removeEventListener('seeked', onSeeked);
                    resolve();
                };
                video.addEventListener('seeked', onSeeked);
            });

            // Create canvas and draw frame
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (aspectRatio === 'square') {
                // For square: crop center of video to square, then scale
                // Use higher resolution for better quality
                const outputSize = 800;
                const videoWidth = video.videoWidth;
                const videoHeight = video.videoHeight;

                // Calculate the square crop area (center crop)
                const cropSize = Math.min(videoWidth, videoHeight);
                const cropX = (videoWidth - cropSize) / 2;
                const cropY = (videoHeight - cropSize) / 2;

                canvas.width = outputSize;
                canvas.height = outputSize;

                // Draw cropped square region scaled to output size
                ctx.drawImage(
                    video,
                    cropX, cropY, cropSize, cropSize,  // Source: center square
                    0, 0, outputSize, outputSize       // Destination: full canvas
                );
            } else {
                // For landscape: scale proportionally with high resolution
                const maxWidth = 1920;
                const maxHeight = 1080;

                let width = video.videoWidth;
                let height = video.videoHeight;

                if (width > maxWidth) {
                    height = (maxWidth / width) * height;
                    width = maxWidth;
                }
                if (height > maxHeight) {
                    width = (maxHeight / height) * width;
                    height = maxHeight;
                }

                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(video, 0, 0, width, height);
            }

            // Convert to blob with high quality
            const blob = await new Promise((resolve) => {
                canvas.toBlob(resolve, 'image/jpeg', 0.95);
            });

            if (blob) {
                const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
                setThumbnailPreview(dataUrl);
                setIsManualUpload(false);

                if (onThumbnailChange) {
                    onThumbnailChange(blob, dataUrl, time);
                }
            }
        } catch (err) {
            setError('שגיאה בחילוץ תמונה מהסרטון');
            console.error('Error extracting thumbnail:', err);
        } finally {
            setIsExtracting(false);
        }
    }, [isVideoLoaded, aspectRatio, onThumbnailChange]);

    // Handle slider change with debounce
    const handleSliderChange = (event, newValue) => {
        setCurrentTime(newValue);

        // Update video preview position
        if (videoRef.current) {
            videoRef.current.currentTime = newValue;
        }

        // Debounce the extraction
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            extractThumbnailFromVideo(newValue);
        }, 300);
    };

    // Handle capture button click
    const handleCapture = () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        extractThumbnailFromVideo(currentTime);
    };

    // Format time for display (mm:ss)
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // If no video source (or pending props) and no external thumbnail, show upload prompt
    if (!videoSource && !videoFile && !videoUrl && !externalThumbnailPreview) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    textAlign: 'center',
                    border: '2px dashed #E2E8F0',
                    borderRadius: '8px',
                    minHeight: '100px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                }}
            >
                <Typography variant="caption" color="textSecondary">
                    בחרו סרטון כדי לחלץ תמונת כיסוי
                </Typography>
            </Paper>
        );
    }

    return (
        <Box>
            <Typography sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: "#6B7280",
                marginBottom: "8px",
                textAlign: "right"
            }}>
                תמונת כיסוי לסרטון
            </Typography>

            {/* Video Preview with Seeker */}
            {videoSource && (
                <Box sx={{ mb: 2 }}>
                    <Box sx={{
                        position: 'relative',
                        backgroundColor: '#000',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        mb: 1
                    }}>
                        <video
                            ref={videoRef}
                            src={videoSource}
                            muted
                            playsInline
                            crossOrigin="anonymous"
                            onLoadedMetadata={handleVideoLoaded}
                            style={{
                                width: '100%',
                                maxHeight: '200px',
                                display: 'block'
                            }}
                        />

                        {/* Capture overlay button */}
                        <Tooltip title="צלם את הפריים הנוכחי">
                            <IconButton
                                onClick={handleCapture}
                                disabled={disabled || isExtracting || !isVideoLoaded}
                                sx={{
                                    position: 'absolute',
                                    bottom: 8,
                                    right: 8,
                                    backgroundColor: 'rgba(87, 131, 126, 0.9)',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: '#57837E'
                                    },
                                    '&:disabled': {
                                        backgroundColor: 'rgba(0,0,0,0.3)'
                                    }
                                }}
                            >
                                {isExtracting ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    <CaptureIcon />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Time slider */}
                    {videoMetadata && (
                        <Box sx={{ px: 1 }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}>
                                <Typography variant="caption" sx={{ minWidth: 40 }}>
                                    {formatTime(0)}
                                </Typography>
                                <ThemeProvider theme={ltrTheme}>
                                    <Slider
                                        value={currentTime}
                                        onChange={handleSliderChange}
                                        min={0}
                                        max={videoMetadata.duration}
                                        step={0.1}
                                        disabled={disabled || !isVideoLoaded}
                                        sx={{
                                            color: '#57837E',
                                            '& .MuiSlider-thumb': {
                                                '&:hover, &.Mui-focusVisible': {
                                                    boxShadow: '0 0 0 8px rgba(87, 131, 126, 0.16)'
                                                }
                                            }
                                        }}
                                    />
                                </ThemeProvider>
                                <Typography variant="caption" sx={{ minWidth: 40 }}>
                                    {formatTime(videoMetadata.duration)}
                                </Typography>
                            </Box>
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
                            >
                                גררו את הסליידר לבחירת פריים לתמונת הכיסוי
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}


            {error && (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: 'block', mt: 1, textAlign: 'right' }}
                >
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default VideoThumbnailExtractor;
